import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CardNotification, Text, Textarea } from "@foris/avocado-suite";
import Modal from "@components/modal";
import styles from "./rejectProposalModal.module.scss";

interface RejectProposalModalProps {
  /** Who sent the proposal, named in the text; a generic wording is used when unknown. */
  requesterName?: string | null;
  isSubmitting: boolean;
  /** Already translated message of the last failed attempt; the reason stays as written. */
  errorMessage?: string | null;
  onCancel: () => void;
  /** Receives the reason trimmed; never called while it is blank. */
  onConfirm: (reason: string) => void;
}

/**
 * Rejection of an assignment proposal with its mandatory reason. The parent mounts it only while it
 * is open, so cancelling clears the reason and a failed attempt keeps it.
 */
const RejectProposalModal = ({
  requesterName,
  isSubmitting,
  errorMessage,
  onCancel,
  onConfirm,
}: RejectProposalModalProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const trimmedReason = reason.trim();

  return (
    <Modal
      typeState="error"
      show
      title={t("assignation-edit.proposal.reject-modal.title")}
      textButtonPrincipal={t("assignation-edit.proposal.reject-modal.confirm")}
      colorButtonPrincipal="danger"
      textButtonSecondary={t("assignation-edit.proposal.reject-modal.cancel")}
      isLoading={isSubmitting}
      isDisabledButtonPrincipal={!trimmedReason || isSubmitting}
      onClose={onCancel}
      onClickSecondary={onCancel}
      onClickPrincipal={() => {
        if (trimmedReason && !isSubmitting) onConfirm(trimmedReason);
      }}
    >
      <div className={styles.rejectProposalModal}>
        <Text>
          {requesterName
            ? t("assignation-edit.proposal.reject-modal.description", { name: requesterName })
            : t("assignation-edit.proposal.reject-modal.description-no-name")}
        </Text>

        <Textarea
          label={t("assignation-edit.proposal.reject-modal.reason.label")}
          placeholder={t("assignation-edit.proposal.reject-modal.reason.placeholder")}
          value={reason}
          required
          disabled={isSubmitting}
          onChange={event => setReason(event.currentTarget.value)}
        />

        {!!errorMessage && (
          <div role="alert">
            <CardNotification state="error" title={errorMessage} outlined />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RejectProposalModal;
